import {
  Modal,
  Button,
  Form,
  Card,
  Tabs,
  Tab,
  Spinner,
  Badge,
  Table,
  Row,
  Col,
} from "react-bootstrap";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import moment from "moment";
import currency from "currency.js";
import {
  FaTruck,
  FaUser,
  FaRoute,
  FaCalendar,
  FaMapMarkerAlt,
  FaGasPump,
  FaClock,
  FaDollarSign,
  FaFileAlt,
  FaEdit,
  FaDownload,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
} from "react-icons/fa";
import TableComponent from "../TableComponent";

function TripDetailModal({ show, onHide, trip, refetch, backendUrl, token }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [loadingData, setLoadingData] = useState(false);

  if (!trip) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (value) => {
    return currency(value || 0, { symbol: "₦", precision: 0 }).format();
  };

  // Helper function to get trip status badge
  const getTripStatusBadge = (status) => {
    const statusMap = {
      planned: "info",
      in_progress: "primary",
      completed: "success",
      cancelled: "danger",
      delayed: "warning",
    };
    return statusMap[status] || "secondary";
  };

  // Helper function to get status icon
  const getStatusIcon = (status) => {
    const iconMap = {
      planned: <FaInfoCircle />,
      in_progress: <FaClock />,
      completed: <FaCheckCircle />,
      cancelled: <FaTimesCircle />,
      delayed: <FaExclamationTriangle />,
    };
    return iconMap[status] || <FaInfoCircle />;
  };

  // Overview Tab Content
  const renderOverviewTab = () => (
    <div>
      {/* Trip Header Card */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-3 align-items-center">
            <FaRoute size={24} className="text-primary" />
            <div>
              <h5 className="mb-0">
                Trip {trip.tripNumber || trip.trip_id?.slice(-8)}
              </h5>
              <small className="text-muted">{trip.purpose}</small>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Badge bg={getTripStatusBadge(trip.status)}>
              {getStatusIcon(trip.status)}
              <span className="ms-1">
                {trip.status?.replace("_", " ").toUpperCase()}
              </span>
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <h6 className="text-muted mb-3">Trip Information</h6>
              <p>
                <strong>Trip ID:</strong> {trip.trip_id}
              </p>
              <p>
                <strong>Purpose:</strong> {trip.purpose}
              </p>
              <p>
                <strong>Planned Distance:</strong> {trip.plannedDistance} miles
              </p>
              <p>
                <strong>Actual Distance:</strong>{" "}
                {trip.actualDistance ? `${trip.actualDistance} km` : "N/A"}
              </p>
              <p>
                <strong>Notes:</strong> {trip.notes || "No notes"}
              </p>
            </Col>
            <Col md={6}>
              <h6 className="text-muted mb-3">Timing</h6>
              <p>
                <FaCalendar className="text-primary me-2" />
                <strong>Planned Start:</strong>{" "}
                {moment(trip.plannedStartTime).format("MMM DD, YYYY HH:mm")}
              </p>
              <p>
                <strong>Actual Start:</strong>{" "}
                {trip.actualStartTime
                  ? moment(trip.actualStartTime).format("MMM DD, YYYY HH:mm")
                  : "Not started"}
              </p>
              <p>
                <strong>Planned End:</strong>{" "}
                {trip.plannedEndTime
                  ? moment(trip.plannedEndTime).format("MMM DD, YYYY HH:mm")
                  : "N/A"}
              </p>
              <p>
                <strong>Actual End:</strong>{" "}
                {trip.actualEndTime
                  ? moment(trip.actualEndTime).format("MMM DD, YYYY HH:mm")
                  : "Not completed"}
              </p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Odometer Information */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">
                {trip.startOdometer?.toLocaleString() || "N/A"}
              </h3>
              <small className="text-muted">Start Odometer (m)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">
                {trip.endOdometer?.toLocaleString() || "N/A"}
              </h3>
              <small className="text-muted">End Odometer (m)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">
                {trip.actualDistance
                  ? trip.actualDistance
                  : trip.plannedDistance}
              </h3>
              <small className="text-muted">Trip Distance  (miles)</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Activity Timeline */}
      <Card>
        <Card.Header>Activity Timeline</Card.Header>
        <Card.Body>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-marker bg-primary" />
              <div className="timeline-content">
                <h6>Trip Created</h6>
                <small className="text-muted">
                  {moment(trip.createdAt).format("MMM DD, YYYY HH:mm")}
                </small>
              </div>
            </div>
            {trip.actualStartTime && (
              <div className="timeline-item">
                <div className="timeline-marker bg-success" />
                <div className="timeline-content">
                  <h6>Trip Started</h6>
                  <small className="text-muted">
                    {moment(trip.actualStartTime).format("MMM DD, YYYY HH:mm")}
                  </small>
                </div>
              </div>
            )}
            {trip.actualEndTime && (
              <div className="timeline-item">
                <div className="timeline-marker bg-info" />
                <div className="timeline-content">
                  <h6>Trip Completed</h6>
                  <small className="text-muted">
                    {moment(trip.actualEndTime).format("MMM DD, YYYY HH:mm")}
                  </small>
                </div>
              </div>
            )}
            {/* <div className="timeline-item">
              <div className="timeline-marker bg-secondary" />
              <div className="timeline-content">
                <h6>Last Updated</h6>
                <small className="text-muted">
                  {moment(trip.updatedAt).format("MMM DD, YYYY HH:mm")}
                </small>
              </div>
            </div> */}
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  // Vehicle Tab Content
  const renderVehicleTab = () => {
    const vehicle = trip.vehicle;
    if (!vehicle) return <p>No vehicle information available</p>;

    return (
      <div>
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-3 align-items-center">
              <FaTruck size={24} className="text-primary" />
              <div>
                <h5 className="mb-0">
                  {vehicle.make} {vehicle.model}
                </h5>
                <small className="text-muted">{vehicle.fleetNumber}</small>
              </div>
            </div>
            <Badge bg={vehicle.isActive ? "success" : "secondary"}>
              {vehicle.isActive ? "Active" : "Inactive"}
            </Badge>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <h6 className="text-muted mb-3">Basic Information</h6>
                <p>
                  <strong>License Plate:</strong> {vehicle.licensePlate}
                </p>
                <p>
                  <strong>VIN:</strong> {vehicle.vin}
                </p>
                <p>
                  <strong>Year:</strong> {vehicle.year}
                </p>
                <p>
                  <strong>Color:</strong> {vehicle.color}
                </p>
                <p>
                  <strong>Fuel Type:</strong> {vehicle.fuelType}
                </p>
              </Col>
              <Col md={6}>
                <h6 className="text-muted mb-3">Specifications</h6>
                <p>
                  <strong>Fuel Capacity:</strong> {vehicle.fuelCapacity} L
                </p>
                <p>
                  <strong>Weight Capacity:</strong> {vehicle.capacityWeight} kg
                </p>
                <p>
                  <strong>Volume Capacity:</strong> {vehicle.capacityVolume} m³
                </p>
                <p>
                  <strong>Current Odometer:</strong>{" "}
                  {vehicle.odometer?.toLocaleString()} km
                </p>
                <p>
                  <strong>Engine Hours:</strong> {vehicle.engineHours}
                </p>
              </Col>
            </Row>
            {vehicle.notes && (
              <div className="mt-3">
                <strong>Notes:</strong>
                <p className="text-muted mb-0">{vehicle.notes}</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Driver Tab Content
  const renderDriverTab = () => {
    const driver = trip.driver;
    if (!driver) return <p>No driver information available</p>;

    return (
      <div>
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-3 align-items-center">
              <FaUser size={24} className="text-primary" />
              <div>
                <h5 className="mb-0">{driver.employee?.Name || "Driver"}</h5>
                <small className="text-muted">
                  Employee ID: {driver.employee_id}
                </small>
              </div>
            </div>
            <Badge bg={driver.status === "active" ? "success" : "secondary"}>
              {driver.status?.toUpperCase()}
            </Badge>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <h6 className="text-muted mb-3">Driver Information</h6>
                <p>
                  <strong>License Number:</strong> {driver.licenseNumber}
                </p>
                <p>
                  <strong>License Class:</strong> {driver.licenseClass}
                </p>
                <p>
                  <strong>License Expiry:</strong>{" "}
                  {moment(driver.licenseExpiry).format("MMM DD, YYYY")}
                </p>
                <p>
                  <strong>Medical Certificate Expiry:</strong>{" "}
                  {moment(driver.medicalCertExpiry).format("MMM DD, YYYY")}
                </p>
                <p>
                  <strong>Safety Score:</strong> {driver.safetyScore}/100
                </p>
              </Col>
              <Col md={6}>
                <h6 className="text-muted mb-3">Contact Information</h6>

                <p>
                  <strong>Contact Phone:</strong> {driver.contactName}
                </p>
                <p>
                  <strong>Emergency Contact:</strong>{" "}
                  {driver.emergencyContactName}
                </p>
                <p>
                  <strong>Emergency Phone:</strong>{" "}
                  {driver.emergencyContactPhone}
                </p>
                <p>
                  <strong>Relationship:</strong>{" "}
                  {driver.emergencyContactRelation}
                </p>
                <p>
                  <strong>Email:</strong> {driver.employee?.email}
                </p>
              </Col>
            </Row>
            {driver.notes && (
              <div className="mt-3">
                <strong>Notes:</strong>
                <p className="text-muted mb-0">{driver.notes}</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Route Tab Content
  const renderRouteTab = () => {
    const route = trip.route;
    const waypoints = trip.route.waypoints
      ? JSON.parse(trip.route.waypoints)
      : [];
    if (!route) return <p>No route information available</p>;

    return (
      <div>
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-3 align-items-center">
              <FaRoute size={24} className="text-primary" />
              <div>
                <h5 className="mb-0">{route.name}</h5>
                <small className="text-muted">Route ID: {route.route_id}</small>
              </div>
            </div>
            <Badge bg={route.isActive ? "success" : "secondary"}>
              {route.isActive ? "Active" : "Inactive"}
            </Badge>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <h6 className="text-muted mb-3">Route Summary</h6>
                <p>
                  <strong>Description:</strong> {route.description || "-"}
                </p>
                <p>
                  <strong>Distance:</strong>{" "}
                  {route.distance != null ? `${route.distance} km` : "N/A"}
                </p>
                <p>
                  <strong>Estimated Duration:</strong>{" "}
                  {route.estimatedDuration
                    ? `${route.estimatedDuration} minutes`
                    : "N/A"}
                </p>
                <div className="mt-3">
                  <strong>Stops:</strong>
                  <div className="mt-2">
                    {(!waypoints || waypoints?.length === 0) && (
                      <small className="text-muted d-block">No waypoints</small>
                    )}
                    {/* Use Bootstrap list-group classes directly to avoid new imports */}
                    {waypoints && waypoints?.length > 0 && (
                      <div className="list-group">
                        {waypoints?.map((wp, i) => (
                          <div
                            key={i}
                            className="list-group-item d-flex justify-content-between align-items-start"
                          >
                            <div className="me-3 d-flex align-items-center">
                              <span className="badge bg-primary me-3">
                                {i + 1}
                              </span>
                              <div>
                                <div className="fw-bold">
                                  {wp.address || wp.name || `Waypoint ${i + 1}`}
                                </div>
                                <small className="text-muted">
                                  {wp.lat?.toFixed(4)}, {wp.lng?.toFixed(4)}
                                </small>
                              </div>
                            </div>
                            <div>
                              <Badge bg="light" text="dark" className="me-2">
                                {wp.lat && wp.lng ? "GPS" : "Manual"}
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() =>
                                  window.open(
                                    `https://www.google.com/maps/search/?api=1&query=${
                                      wp.lat
                                    },${wp.lng}`,
                                    "_blank"
                                  )
                                }
                              >
                                Open
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Col>

            </Row>
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Expenses Tab Content
  const renderExpensesTab = () => {
    const expenses = trip.expenses || [];

    const expensesTableHead = () => (
      <thead>
        <tr>
          <th>Date</th>
          <th>Category</th>
          <th>Description</th>
          <th>Amount</th>
          <th>Status</th>
        </tr>
      </thead>
    );

    const expensesDisplayTable = (expense) => (
      <>
        <td>{moment(expense.expenseDate).format("MMM DD, YYYY")}</td>
        <td>{expense.category}</td>
        <td>{expense.description}</td>
        <td className="text-end">{formatCurrency(expense.amount)}</td>
        <td>
          <Badge bg="success">Approved</Badge>
        </td>
      </>
    );

    return (
      <div>
        {expenses.length > 0 ? (
          <TableComponent
            responsive
            borderless
            striped
            tableHeadsFunction={expensesTableHead}
            mainDataArray={expenses}
            tableDataRowFunction={expensesDisplayTable}
            className="product-table text-nowrap"
          />
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5">
              <FaDollarSign size={48} className="mb-3 opacity-50" />
              <p>No expenses recorded for this trip</p>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  // Documents Tab Content
  const renderDocumentsTab = () => {
    const documents = trip.documents || [];

    const documentsTableHead = () => (
      <thead>
        <tr>
          <th>Title</th>
          <th>Type</th>
          <th>Description</th>
          <th>Uploaded</th>
          <th>Actions</th>
        </tr>
      </thead>
    );

    const documentsDisplayTable = (doc) => (
      <>
        <td>{doc.title}</td>
        <td>
          <Badge bg="info">{doc.documentType}</Badge>
        </td>
        <td>{doc.description || "-"}</td>
        <td>{moment(doc.createdAt).format("MMM DD, YYYY")}</td>
        <td>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              window.open(`${backendUrl}/static${doc.filePath}`, "_blank")
            }
          >
            <FaDownload />
          </Button>
        </td>
      </>
    );

    return (
      <div>
        {documents.length > 0 ? (
          <TableComponent
            responsive
            borderless
            striped
            tableHeadsFunction={documentsTableHead}
            mainDataArray={documents}
            tableDataRowFunction={documentsDisplayTable}
            className="product-table text-nowrap"
          />
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5">
              <FaFileAlt size={48} className="mb-3 opacity-50" />
              <p>No documents uploaded for this trip</p>
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };
  console.log("redering modal");
  return (
    <Modal
      show={show}
      onHide={() => {
        console.log("closing modal");
        onHide();
      }}
      backdropClassName="global-backdrop"
      centered
      dialogClassName="my-style-modal"
      animation={false}
      enforceFocus={false}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div>
            <h4>Trip Details - {trip.tripNumber || trip.trip_id?.slice(-8)}</h4>
            <small className="text-muted">{trip.purpose}</small>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="container-fluid">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || "overview")}
            className="mb-3"
          >
            <Tab eventKey="overview" title="Overview">
              {renderOverviewTab()}
            </Tab>

            <Tab eventKey="vehicle" title="Vehicle">
              {renderVehicleTab()}
            </Tab>

            <Tab eventKey="driver" title="Driver">
              {renderDriverTab()}
            </Tab>

            <Tab eventKey="route" title="Route">
              {renderRouteTab()}
            </Tab>

            <Tab
              eventKey="expenses"
              title={`Expenses (${trip.expenses?.length || 0})`}
            >
              {renderExpensesTab()}
            </Tab>

            <Tab
              eventKey="documents"
              title={`Documents (${trip.documents?.length || 0})`}
            >
              {renderDocumentsTab()}
            </Tab>
          </Tabs>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
        <Button
          variant="outline-primary"
          onClick={() => {
            // Handle edit action
            console.log("Edit trip:", trip.trip_id);
          }}
        >
          <FaEdit className="me-2" />
          Edit Trip
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default TripDetailModal;
